import { RegisterAssetVariantsResponse, type AdminAsset, type AssetRef } from '@diana/contracts';
import * as Dialog from '@radix-ui/react-dialog';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ImagePlus,
  LoaderCircle,
  Move,
  RefreshCw,
  RotateCcw,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';

import { SortableList } from '../../components/SortableList.js';
import { useDianaUIPortal } from '../../ui/provider/DianaUIProvider.js';
import { useAdminApi } from '../../app/AdminApiContext.js';
import {
  createMediaUploadController,
  type MediaUploadApi,
  type MediaUploadStatus,
  type UploadFile,
} from '../../lib/media-upload.js';
import { uploadResponsiveImage, type PreparedResponsiveVariant } from '../../lib/image-variants.js';
import { Badge, Button, Typography } from '../../ui/index.js';
import { cropForFocus, cropPixelSize, type NormalizedCrop } from './image-crop.js';
import {
  imageCropProfile,
  imageSlotProfile,
  type ImageCropProfileKey,
  type ImageSlotProfileKey,
} from './image-slots.js';
import './ContextualMediaField.css';

type SharedMediaFieldProps = {
  assets: readonly AdminAsset[];
  title: string;
  description?: string;
  profile: ImageSlotProfileKey;
};

export function ContextualMediaField({
  assets,
  title,
  profile,
  cropProfile,
  value,
  onChange,
}: SharedMediaFieldProps & {
  cropProfile: ImageCropProfileKey;
  value: AssetRef | undefined;
  onChange(value: AssetRef): void;
}) {
  const titleId = useId();
  const fileInput = useRef<HTMLInputElement>(null);
  const [replacement, setReplacement] = useState<UploadFile | null>(null);
  const [uploaded, setUploaded] = useState<AdminAsset[]>([]);
  const availableAssets = mergeAssets(assets, uploaded);
  const selected = availableAssets.find((asset) => asset.id === value?.assetId);
  const currentAlt = value?.altOverride ?? selected?.alt ?? '';

  const chooseReplacement = () => fileInput.current?.click();

  return (
    <section className="contextual-media-field" aria-labelledby={titleId}>
      <header className="contextual-media-field__header">
        <Typography as="h3" variant="h3" id={titleId}>
          {title}
        </Typography>
      </header>

      <input
        ref={fileInput}
        className="contextual-media-field__file-input"
        aria-label={`Choose replacement for ${title}`}
        type="file"
        accept="image/jpeg,image/webp"
        onChange={(event) => {
          const file = event.currentTarget.files?.[0] as UploadFile | undefined;
          if (file) setReplacement(file);
          event.currentTarget.value = '';
        }}
      />

      {replacement ? (
        <ContextualCropReplacement
          title={title}
          file={replacement}
          profile={profile}
          cropProfile={cropProfile}
          initialAlt={currentAlt}
          onCancel={() => setReplacement(null)}
          onReady={(asset) => {
            setUploaded((current) => mergeAssets(current, [asset]));
            onChange({ assetId: asset.id });
            setReplacement(null);
          }}
        />
      ) : selected && value ? (
        <div className="contextual-media-field__current">
          <div className="contextual-media-field__summary">
            <figure>
              <img src={previewUrl(selected)} alt="" />
              <Button
                className="contextual-media-field__replace"
                variant="primary"
                leadingIcon={ImagePlus}
                onClick={chooseReplacement}
              >
                Replace image
              </Button>
            </figure>
            <div className="contextual-media-field__file">
              <Typography variant="label">{selected.originalFilename}</Typography>
              <Typography variant="caption">{assetFileMeta(selected)}</Typography>
            </div>
          </div>
          <PlacementAltField
            label={`${title} Alt Text`}
            asset={selected}
            value={value}
            onChange={onChange}
          />
        </div>
      ) : (
        <div className="contextual-media-field__empty">
          <ImagePlus size={22} aria-hidden="true" />
          <Button variant="primary" onClick={chooseReplacement}>
            Upload image
          </Button>
        </div>
      )}
    </section>
  );
}

export function ContextualGalleryField<T extends { media: AssetRef }>({
  assets,
  title,
  description,
  profile,
  items,
  createItem,
  onChange,
  selectionEditor,
}: SharedMediaFieldProps & {
  items: readonly T[];
  createItem(media: AssetRef): T;
  onChange(items: T[]): void;
  /** Gallery editor only; other field consumers retain their current presentation. */
  selectionEditor?: { compact: boolean };
}) {
  const titleId = useId();
  const [panel, setPanel] = useState<'closed' | 'upload'>('closed');
  const [uploaded, setUploaded] = useState<AdminAsset[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(items[0]?.media.assetId ?? null);
  const [quickEdit, setQuickEdit] = useState(false);
  const selectionTrigger = useRef<HTMLButtonElement | null>(null);
  const fieldRoot = useRef<HTMLElement | null>(null);
  const availableAssets = mergeAssets(assets, uploaded);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const selected = items.find((item) => item.media.assetId === selectedId) ?? items[0];
  const selectedAsset = availableAssets.find((asset) => asset.id === selected?.media.assetId);
  const selectedIndex = items.findIndex((item) => item === selected);

  const changeItems = (next: T[]) => {
    itemsRef.current = next;
    onChange(next);
  };
  const addAsset = (asset: AdminAsset) => {
    const current = itemsRef.current;
    if (!current.some((item) => item.media.assetId === asset.id)) {
      changeItems([...current, createItem({ assetId: asset.id })]);
    }
  };
  const move = (from: number, to: number) => {
    const current = itemsRef.current;
    if (to < 0 || to >= current.length) return;
    const next = [...current];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved!);
    changeItems(next);
  };
  const remove = (assetId: string) => {
    if (itemsRef.current.length <= 1) return;
    const next = itemsRef.current.filter((item) => item.media.assetId !== assetId);
    if (selected?.media.assetId === assetId) setSelectedId(next[0]?.media.assetId ?? null);
    changeItems(next);
  };
  const selectionDetails = selected ? (
    <div className="gallery-photo-details" aria-label="Selected photograph">
      {selectedAsset ? (
        <img className="gallery-photo-details__preview" src={previewUrl(selectedAsset)} alt="" />
      ) : null}
      <strong>
        Photo {selectedIndex + 1} of {items.length}
      </strong>
      <p>{selectedAsset?.originalFilename ?? 'Photograph unavailable'}</p>
      {selectedAsset ? (
        <PlacementAltField
          key={selected.media.assetId}
          label="Alt text for selected photograph"
          asset={selectedAsset}
          value={selected.media}
          resetEmptyToInherited
          onChange={(media) => {
            changeItems(
              itemsRef.current.map((item) =>
                item.media.assetId === selected.media.assetId ? { ...item, media } : item,
              ),
            );
          }}
        />
      ) : (
        <p className="inline-note">
          This asset is unavailable. You can reorder or remove its gallery placement.
        </p>
      )}
      <p className="inline-note">
        Describes this placement only. Clear the field to use the original image alt text.
      </p>
      <div className="gallery-photo-details__actions">
        <Button
          variant="secondary"
          disabled={selectedIndex <= 0}
          onClick={() => move(selectedIndex, selectedIndex - 1)}
        >
          ← Earlier
        </Button>
        <Button
          variant="secondary"
          disabled={selectedIndex >= items.length - 1}
          onClick={() => move(selectedIndex, selectedIndex + 1)}
        >
          Later →
        </Button>
      </div>
      <Button
        variant="quiet"
        disabled={items.length <= 1}
        onClick={() => remove(selected.media.assetId)}
      >
        Remove from gallery
      </Button>
    </div>
  ) : null;

  const renderItem = (item: T, index: number) => {
    const asset = availableAssets.find((candidate) => candidate.id === item.media.assetId);
    if (!asset && !selectionEditor) return null;
    return (
      <article
        className="contextual-gallery-item"
        key={selectionEditor ? item.media.assetId : `${item.media.assetId}-${index}`}
        data-selected={
          (selectionEditor && selected?.media.assetId === item.media.assetId) || undefined
        }
      >
        <div className="contextual-gallery-item__image">
          {selectionEditor ? (
            <button
              className="gallery-photo-select"
              type="button"
              aria-label={`Edit photograph ${index + 1}`}
              aria-pressed={selected?.media.assetId === item.media.assetId}
              onClick={(event) => {
                selectionTrigger.current = event.currentTarget;
                setSelectedId(item.media.assetId);
                setQuickEdit(true);
              }}
            >
              {asset ? <img src={previewUrl(asset)} alt="" /> : <span>Photograph unavailable</span>}
            </button>
          ) : asset ? (
            <img src={previewUrl(asset)} alt="" />
          ) : null}
          <span className="contextual-gallery-item__position">
            {String(index + 1).padStart(2, '0')}
          </span>
          <span className="contextual-gallery-item__filename">
            {asset?.originalFilename ?? 'Unavailable asset'}
          </span>
          <div className="contextual-gallery-item__actions">
            <button
              type="button"
              aria-label={`Move image ${index + 1} earlier`}
              disabled={index === 0}
              onClick={() => move(index, index - 1)}
            >
              <ArrowLeft size={15} aria-hidden="true" />
            </button>
            <button
              type="button"
              aria-label={`Move image ${index + 1} later`}
              disabled={index === items.length - 1}
              onClick={() => move(index, index + 1)}
            >
              <ArrowRight size={15} aria-hidden="true" />
            </button>
            <button
              type="button"
              aria-label={`Remove image ${index + 1}`}
              disabled={items.length === 1}
              onClick={() =>
                selectionEditor
                  ? remove(item.media.assetId)
                  : items.length > 1 &&
                    changeItems(items.filter((_, itemIndex) => itemIndex !== index))
              }
            >
              <Trash2 size={15} aria-hidden="true" />
            </button>
          </div>
        </div>
      </article>
    );
  };

  return (
    <section
      ref={fieldRoot}
      className={`contextual-gallery-field${selectionEditor ? ' contextual-gallery-field--editable' : ''}`}
      aria-labelledby={titleId}
    >
      <header className="contextual-media-field__header">
        <div>
          <Typography as="h3" variant="h3" id={titleId}>
            {title}
          </Typography>
          {description ? <Typography variant="small">{description}</Typography> : null}
        </div>
        <Typography variant="caption">
          {items.length} {items.length === 1 ? 'photograph' : 'photographs'}
        </Typography>
      </header>
      <div className="contextual-gallery-field__toolbar">
        <Button
          variant="primary"
          leadingIcon={Upload}
          aria-expanded={panel === 'upload'}
          onClick={() => setPanel(panel === 'upload' ? 'closed' : 'upload')}
        >
          Add photographs
        </Button>
      </div>
      {panel === 'upload' ? (
        <ContextualUploadQueue
          title={`Add to ${title.toLowerCase()}`}
          multiple
          profile={profile}
          onClose={() => setPanel('closed')}
          onReady={(asset) => {
            setUploaded((current) => mergeAssets(current, [asset]));
            addAsset(asset);
          }}
        />
      ) : null}
      {selectionEditor ? (
        <div className="gallery-photo-layout">
          <SortableList
            layout="grid"
            items={items.map((item) => ({ id: item.media.assetId, item }))}
            label={(entry) =>
              `image ${items.findIndex((item) => item.media.assetId === entry.id) + 1}`
            }
            onChange={(next) => changeItems(next.map((entry) => entry.item))}
            render={(entry, index) => renderItem(entry.item, index)}
          />
          {selectionEditor.compact ? (
            <GalleryPhotoSheet
              open={quickEdit && !!selected}
              onOpenChange={setQuickEdit}
              returnFocus={() => {
                if (selectionTrigger.current?.isConnected) selectionTrigger.current.focus();
                else
                  fieldRoot.current
                    ?.querySelector<HTMLButtonElement>('.gallery-photo-select[aria-pressed="true"]')
                    ?.focus();
              }}
            >
              {selectionDetails}
            </GalleryPhotoSheet>
          ) : (
            <aside aria-label="Selected photograph details">{selectionDetails}</aside>
          )}
        </div>
      ) : (
        <div className="contextual-gallery-field__grid">{items.map(renderItem)}</div>
      )}
    </section>
  );
}

function GalleryPhotoSheet({
  open,
  onOpenChange,
  returnFocus,
  children,
}: {
  open: boolean;
  onOpenChange(open: boolean): void;
  returnFocus(): void;
  children: React.ReactNode;
}) {
  const portal = useDianaUIPortal();
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next && document.activeElement instanceof HTMLElement) document.activeElement.blur();
        onOpenChange(next);
      }}
    >
      <Dialog.Portal container={portal}>
        <Dialog.Overlay className="gallery-sheet-overlay" />
        <Dialog.Content
          className="gallery-sheet gallery-photo-sheet"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            returnFocus();
          }}
        >
          <Dialog.Title>Edit photograph</Dialog.Title>
          <Dialog.Description>Alt text and order in this gallery.</Dialog.Description>
          {children}
          <Dialog.Close asChild>
            <Button variant="primary">Done</Button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function PlacementAltField({
  label,
  asset,
  value,
  onChange,
  resetEmptyToInherited = false,
}: {
  resetEmptyToInherited?: boolean;
  label: string;
  asset: AdminAsset;
  value: AssetRef;
  onChange(value: AssetRef): void;
}) {
  const [draft, setDraft] = useState(value.altOverride ?? asset.alt);
  const edited = useRef(false);

  useEffect(() => {
    setDraft(value.altOverride ?? asset.alt);
    edited.current = false;
  }, [asset.alt, asset.id, value.altOverride]);

  const commit = () => {
    if (resetEmptyToInherited && !edited.current) return;
    const alt = draft.trim();
    if (!alt && !resetEmptyToInherited) {
      setDraft(value.altOverride ?? asset.alt);
      return;
    }
    if (resetEmptyToInherited) {
      edited.current = false;
      const { altOverride: _previous, ...inherited } = value;
      onChange({ ...inherited, ...(alt && alt !== asset.alt ? { altOverride: alt } : {}) });
      setDraft(alt || asset.alt);
    } else {
      onChange({ assetId: value.assetId, ...(alt === asset.alt ? {} : { altOverride: alt }) });
    }
  };

  return (
    <label className="contextual-media-alt">
      <span>{label}</span>
      <input
        className="dui-input"
        value={draft}
        onChange={(event) => {
          edited.current = true;
          setDraft(event.currentTarget.value);
        }}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur();
        }}
      />
    </label>
  );
}

function ContextualCropReplacement({
  title,
  file,
  profile,
  cropProfile,
  initialAlt,
  onReady,
  onCancel,
}: {
  title: string;
  file: UploadFile;
  profile: ImageSlotProfileKey;
  cropProfile: ImageCropProfileKey;
  initialAlt: string;
  onReady(asset: AdminAsset): void;
  onCancel(): void;
}) {
  const [source, setSource] = useState<{ width: number; height: number } | null>(null);
  const [sourceError, setSourceError] = useState<string | null>(null);
  const [focus, setFocus] = useState({ x: 0.5, y: 0.5 });
  const [zoom, setZoom] = useState(1);
  const [alt, setAlt] = useState(initialAlt);
  const preview = useObjectUrl(file);
  const cropContract = imageCropProfile(cropProfile);
  const slot = imageSlotProfile(profile);
  const crop = source
    ? cropForFocus({
        sourceWidth: source.width,
        sourceHeight: source.height,
        targetWidth: cropContract.width,
        targetHeight: cropContract.height,
        focusX: focus.x,
        focusY: focus.y,
        zoom,
      })
    : null;
  const cropPixels = source && crop ? cropPixelSize(crop, source) : null;
  const requiredWidth = slot.widths[1];
  const requiredHeight = Math.round((requiredWidth * cropContract.height) / cropContract.width);
  const enoughPixels =
    cropPixels !== null && cropPixels.width >= requiredWidth && cropPixels.height >= requiredHeight;
  const upload = useResponsiveUploadTask({ file, profile, crop, onReady });
  const drag = useRef<{
    pointerId: number;
    clientX: number;
    clientY: number;
    focusX: number;
    focusY: number;
    width: number;
    height: number;
  } | null>(null);

  useEffect(() => {
    let active = true;
    setSource(null);
    setSourceError(null);
    void createImageBitmap(file, { imageOrientation: 'from-image' })
      .then((bitmap) => {
        if (active) setSource({ width: bitmap.width, height: bitmap.height });
        bitmap.close();
      })
      .catch(() => {
        if (active) setSourceError('This photograph could not be opened');
      });
    return () => {
      active = false;
    };
  }, [file]);

  const moveFocus = (x: number, y: number) => {
    setFocus({ x: clamp(x, 0, 1), y: clamp(y, 0, 1) });
  };

  return (
    <section className="contextual-crop" aria-label={`Replace ${title.toLowerCase()}`}>
      <header className="contextual-crop__header">
        <div>
          <Typography as="h4" variant="h3">
            Adjust crop
          </Typography>
          <Typography variant="small">
            {cropContract.label} · {slot.recommendation}
          </Typography>
        </div>
        <button type="button" aria-label="Cancel image replacement" onClick={onCancel}>
          <X size={18} aria-hidden="true" />
        </button>
      </header>

      <div className="contextual-crop__canvas">
        {source && preview ? (
          <div
            className="contextual-crop-stage"
            style={{
              aspectRatio: `${source.width} / ${source.height}`,
              width: `min(100%, ${Math.round(Math.min(760, (source.width / source.height) * 520))}px)`,
            }}
          >
            <img src={preview} alt="" />
            {crop ? (
              <button
                type="button"
                className="contextual-crop-stage__window"
                aria-label="Crop area. Drag or use arrow keys to reposition"
                style={{
                  left: `${crop.x * 100}%`,
                  top: `${crop.y * 100}%`,
                  width: `${crop.width * 100}%`,
                  height: `${crop.height * 100}%`,
                }}
                onPointerDown={(event) => {
                  const bounds = event.currentTarget.parentElement?.getBoundingClientRect();
                  if (!bounds?.width || !bounds.height) return;
                  event.currentTarget.setPointerCapture?.(event.pointerId);
                  drag.current = {
                    pointerId: event.pointerId,
                    clientX: event.clientX,
                    clientY: event.clientY,
                    focusX: focus.x,
                    focusY: focus.y,
                    width: bounds.width,
                    height: bounds.height,
                  };
                }}
                onPointerMove={(event) => {
                  const start = drag.current;
                  if (!start || start.pointerId !== event.pointerId) return;
                  moveFocus(
                    start.focusX + (event.clientX - start.clientX) / start.width,
                    start.focusY + (event.clientY - start.clientY) / start.height,
                  );
                }}
                onPointerUp={() => {
                  drag.current = null;
                }}
                onPointerCancel={() => {
                  drag.current = null;
                }}
                onKeyDown={(event) => {
                  const step = event.shiftKey ? 0.05 : 0.01;
                  const movement = {
                    ArrowLeft: [-step, 0],
                    ArrowRight: [step, 0],
                    ArrowUp: [0, -step],
                    ArrowDown: [0, step],
                  }[event.key];
                  if (!movement) return;
                  event.preventDefault();
                  moveFocus(focus.x + movement[0], focus.y + movement[1]);
                }}
              >
                <Move size={20} aria-hidden="true" />
              </button>
            ) : null}
          </div>
        ) : (
          <div className="contextual-crop__loading">{sourceError ?? 'Opening photograph'}</div>
        )}
      </div>

      <div className="contextual-crop__details">
        <div className="contextual-crop__file">
          <Typography variant="label">{file.name}</Typography>
          <Typography variant="caption">
            {source ? `${source.width} × ${source.height} px` : 'Reading image dimensions'}
          </Typography>
        </div>
        <label className="contextual-crop__zoom">
          <span>Zoom</span>
          <input
            aria-label="Crop zoom"
            type="range"
            min="1"
            max="3"
            step="0.05"
            value={zoom}
            onChange={(event) => setZoom(Number(event.currentTarget.value))}
          />
        </label>
        <label className="contextual-media-alt">
          <span>Alt text</span>
          <input
            className="dui-input"
            value={alt}
            disabled={upload.busy || upload.status === 'Ready'}
            onChange={(event) => setAlt(event.currentTarget.value)}
          />
        </label>
        {cropPixels ? (
          <Typography
            className={enoughPixels ? undefined : 'contextual-crop__warning'}
            variant="caption"
          >
            {enoughPixels
              ? `Selected area ${cropPixels.width} × ${cropPixels.height} px`
              : `Selected area is too small. Keep at least ${requiredWidth} × ${requiredHeight} px.`}
          </Typography>
        ) : null}
        {upload.error ? (
          <Typography className="contextual-crop__warning" variant="caption">
            {upload.error.message}
          </Typography>
        ) : null}
        {upload.busy ? (
          <progress max={100} value={upload.progress}>
            {upload.progress}%
          </progress>
        ) : null}
      </div>

      <footer className="contextual-crop__actions">
        <Button variant="quiet" onClick={onCancel} disabled={upload.busy}>
          Cancel
        </Button>
        <Button
          variant="secondary"
          leadingIcon={RotateCcw}
          disabled={upload.busy || (focus.x === 0.5 && focus.y === 0.5 && zoom === 1)}
          onClick={() => {
            setFocus({ x: 0.5, y: 0.5 });
            setZoom(1);
          }}
        >
          Reset
        </Button>
        <Button
          variant="primary"
          leadingIcon={upload.status === 'Error' ? RefreshCw : ImagePlus}
          disabled={upload.busy || !alt.trim() || !enoughPixels}
          onClick={() => void upload.start(alt)}
        >
          {upload.status === 'Error' ? 'Retry' : upload.busy ? 'Uploading' : 'Use image'}
        </Button>
      </footer>
    </section>
  );
}

function useResponsiveUploadTask({
  file,
  profile,
  crop,
  onReady,
}: {
  file: UploadFile;
  profile: ImageSlotProfileKey;
  crop: NormalizedCrop | null;
  onReady(asset: AdminAsset): void;
}) {
  const api = useAdminApi();
  const queryClient = useQueryClient();
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  const controllers = useRef<ReturnType<typeof createMediaUploadController>[]>([]);
  const [status, setStatus] = useState<MediaUploadStatus>('Selection');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<Error | null>(null);
  const busy = ['Preparing', 'Signing', 'Uploading', 'Confirming'].includes(status);

  useEffect(() => () => controllers.current.forEach((controller) => controller.dispose()), []);

  const start = async (alt: string) => {
    if (!crop) return;
    controllers.current.forEach((controller) => controller.dispose());
    controllers.current = [];
    setStatus('Preparing');
    setProgress(0);
    setError(null);
    let completed = 0;
    const total = imageSlotProfile(profile).widths.length;
    try {
      const asset = await uploadResponsiveImage<AdminAsset>({
        file,
        alt,
        crop,
        profile: imageSlotProfile(profile),
        async uploadVariant(variant, variantAlt) {
          const controller = variantController(variant, api as MediaUploadApi);
          controllers.current.push(controller);
          const unsubscribe = controller.subscribe(() => {
            const snapshot = controller.getSnapshot();
            setStatus(snapshot.status === 'Selection' ? 'Preparing' : snapshot.status);
            const partial = snapshot.status === 'Uploading' ? snapshot.progress / 100 : 0;
            setProgress(Math.round(((completed + partial) / total) * 100));
          });
          try {
            if (!controller.select({ file: variant.file, alt: variantAlt })) {
              throw controller.getSnapshot().error ?? new Error('Prepared image is invalid');
            }
            await controller.start();
            const snapshot = controller.getSnapshot();
            if (snapshot.status !== 'Ready' || !snapshot.asset) {
              throw snapshot.error ?? new Error('Responsive image upload failed');
            }
            completed += 1;
            setProgress(Math.round((completed / total) * 100));
            return snapshot.asset;
          } finally {
            unsubscribe();
          }
        },
        async register(canonicalAssetId, variants) {
          setStatus('Confirming');
          RegisterAssetVariantsResponse.parse(
            await api.request(`/api/v1/admin/assets/${canonicalAssetId}/variants`, {
              method: 'POST',
              body: { variants },
              schema: RegisterAssetVariantsResponse,
            }),
          );
        },
      });
      await queryClient.invalidateQueries({ queryKey: ['assets'] });
      setProgress(100);
      setStatus('Ready');
      onReadyRef.current(asset);
    } catch (caught) {
      setStatus('Error');
      setError(caught instanceof Error ? caught : new Error('Responsive image upload failed'));
    }
  };

  return { status, progress, error, busy, start };
}

function ContextualUploadQueue({
  title,
  multiple,
  profile,
  onReady,
  onClose,
}: {
  title: string;
  multiple: boolean;
  profile: ImageSlotProfileKey;
  onReady(asset: AdminAsset): void;
  onClose(): void;
}) {
  const [files, setFiles] = useState<Array<{ id: string; file: UploadFile }>>([]);

  return (
    <section className="contextual-upload" role="region" aria-label={title}>
      <header>
        <div>
          <Typography variant="eyebrow">Private R2 upload</Typography>
          <Typography as="h4" variant="h3">
            {title}
          </Typography>
          <Typography variant="small">{imageSlotProfile(profile).recommendation}</Typography>
        </div>
        <button type="button" aria-label="Close upload queue" onClick={onClose}>
          <X size={18} aria-hidden="true" />
        </button>
      </header>
      <label className="contextual-upload__picker">
        <Upload size={19} aria-hidden="true" />
        <span>
          <strong>{multiple ? 'Choose photographs' : 'Choose a photograph'}</strong>
          <small>Previews appear here before anything is uploaded.</small>
        </span>
        <input
          aria-label={multiple ? 'Choose photographs to upload' : 'Choose a photograph to upload'}
          type="file"
          accept="image/jpeg,image/webp"
          multiple={multiple}
          onChange={(event) => {
            const selected = Array.from(event.currentTarget.files ?? []) as UploadFile[];
            setFiles((current) => [
              ...(multiple ? current : []),
              ...selected.map((file) => ({ id: crypto.randomUUID(), file })),
            ]);
            event.currentTarget.value = '';
          }}
        />
      </label>
      {files.length > 0 ? (
        <div className="contextual-upload__queue">
          {files.map((entry) => (
            <UploadQueueItem
              key={entry.id}
              file={entry.file}
              profile={profile}
              onReady={onReady}
              onRemove={() => setFiles((current) => current.filter((item) => item.id !== entry.id))}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}

function UploadQueueItem({
  file,
  profile,
  onReady,
  onRemove,
}: {
  file: UploadFile;
  profile: ImageSlotProfileKey;
  onReady(asset: AdminAsset): void;
  onRemove(): void;
}) {
  const api = useAdminApi();
  const queryClient = useQueryClient();
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  const controllers = useRef<ReturnType<typeof createMediaUploadController>[]>([]);
  const [status, setStatus] = useState<
    'Selection' | 'Preparing' | 'Signing' | 'Uploading' | 'Confirming' | 'Ready' | 'Error'
  >('Selection');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<Error | null>(null);
  const [alt, setAlt] = useState('');
  const preview = useObjectUrl(file);
  const busy = ['Preparing', 'Signing', 'Uploading', 'Confirming'].includes(status);

  useEffect(() => () => controllers.current.forEach((controller) => controller.dispose()), []);

  const start = async () => {
    controllers.current.forEach((controller) => controller.dispose());
    controllers.current = [];
    setStatus('Preparing');
    setProgress(0);
    setError(null);
    let completed = 0;
    try {
      const asset = await uploadResponsiveImage<AdminAsset>({
        file,
        alt,
        profile: imageSlotProfile(profile),
        async uploadVariant(variant, variantAlt) {
          const controller = variantController(variant, api as MediaUploadApi);
          controllers.current.push(controller);
          const unsubscribe = controller.subscribe(() => {
            const snapshot = controller.getSnapshot();
            setStatus(snapshot.status === 'Selection' ? 'Preparing' : snapshot.status);
            const partial = snapshot.status === 'Uploading' ? snapshot.progress / 100 : 0;
            setProgress(Math.round(((completed + partial) / 2) * 100));
          });
          try {
            if (!controller.select({ file: variant.file, alt: variantAlt })) {
              throw controller.getSnapshot().error ?? new Error('Prepared image is invalid');
            }
            await controller.start();
            const snapshot = controller.getSnapshot();
            if (snapshot.status !== 'Ready' || !snapshot.asset) {
              throw snapshot.error ?? new Error('Responsive image upload failed');
            }
            completed += 1;
            setProgress(Math.round((completed / 2) * 100));
            return snapshot.asset;
          } finally {
            unsubscribe();
          }
        },
        async register(canonicalAssetId, variants) {
          setStatus('Confirming');
          RegisterAssetVariantsResponse.parse(
            await api.request(`/api/v1/admin/assets/${canonicalAssetId}/variants`, {
              method: 'POST',
              body: { variants },
              schema: RegisterAssetVariantsResponse,
            }),
          );
        },
      });
      await queryClient.invalidateQueries({ queryKey: ['assets'] });
      setProgress(100);
      setStatus('Ready');
      onReadyRef.current(asset);
    } catch (caught) {
      setStatus('Error');
      setError(caught instanceof Error ? caught : new Error('Responsive image upload failed'));
    }
  };

  return (
    <article className={`contextual-upload-row is-${status.toLowerCase()}`}>
      <figure>
        {preview ? <img src={preview} alt="" /> : null}
        <span>{uploadIcon(status)}</span>
      </figure>
      <div className="contextual-upload-row__body">
        <div>
          <Typography variant="label">{file.name}</Typography>
          <Typography variant="caption">{uploadDetail(status, error)}</Typography>
        </div>
        <label>
          <span>Alt text</span>
          <input
            className="dui-input"
            value={alt}
            disabled={busy || status === 'Ready'}
            placeholder="Describe what is visible in the photograph"
            onChange={(event) => setAlt(event.currentTarget.value)}
          />
        </label>
        {status === 'Uploading' ? (
          <progress max={100} value={progress}>
            {progress}%
          </progress>
        ) : null}
      </div>
      <div className="contextual-upload-row__actions">
        {status === 'Ready' ? (
          <Badge tone="success">Ready</Badge>
        ) : (
          <Button
            variant="secondary"
            size="small"
            leadingIcon={status === 'Error' ? RefreshCw : Upload}
            disabled={busy || !alt.trim()}
            onClick={() => void start()}
          >
            {status === 'Error' ? 'Retry' : busy ? 'Uploading' : 'Upload'}
          </Button>
        )}
        {!busy ? (
          <button type="button" aria-label={`Remove ${file.name} from queue`} onClick={onRemove}>
            <X size={16} aria-hidden="true" />
          </button>
        ) : null}
      </div>
    </article>
  );
}

function useObjectUrl(file: Blob) {
  const [preview, setPreview] = useState<{ file: Blob; url: string } | null>(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setPreview({ file, url });
    return () => URL.revokeObjectURL(url);
  }, [file]);

  return preview?.file === file ? preview.url : null;
}

function uploadIcon(
  status: ReturnType<ReturnType<typeof createMediaUploadController>['getSnapshot']>['status'],
) {
  if (status === 'Ready') return <CheckCircle2 size={17} aria-hidden="true" />;
  if (status === 'Error') return <AlertCircle size={17} aria-hidden="true" />;
  if (['Preparing', 'Signing', 'Uploading', 'Confirming'].includes(status)) {
    return <LoaderCircle className="contextual-upload-row__spinner" size={17} aria-hidden="true" />;
  }
  return <Upload size={17} aria-hidden="true" />;
}

function uploadDetail(
  status: ReturnType<ReturnType<typeof createMediaUploadController>['getSnapshot']>['status'],
  error: Error | null,
) {
  if (error) return error.message;
  return {
    Selection: 'Ready to upload',
    Preparing: 'Checking image dimensions and checksum',
    Signing: 'Preparing a private upload',
    Uploading: 'Uploading to private storage',
    Confirming: 'Verifying uploaded bytes',
    Ready: 'Optimized and available in Media',
    Error: 'Upload needs attention',
  }[status];
}

function variantController(variant: PreparedResponsiveVariant, api: MediaUploadApi) {
  return createMediaUploadController({
    api,
    prepare: async () => ({
      checksum: variant.checksum,
      width: variant.width,
      height: variant.height,
    }),
  });
}

function previewUrl(asset: AdminAsset) {
  return `/api/v1/admin/assets/${asset.id}/preview`;
}

function assetFileMeta(asset: AdminAsset) {
  const extension =
    asset.format?.toUpperCase() ??
    asset.originalFilename.split('.').pop()?.toUpperCase() ??
    'IMAGE';
  return `${extension} · ${formatBytes(asset.byteSize)}`;
}

function formatBytes(value: number | null) {
  if (value === null) return 'Size unavailable';
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`;
  return `${(value / (1024 * 1024)).toFixed(value < 10 * 1024 * 1024 ? 1 : 0)} MB`;
}

function mergeAssets(first: readonly AdminAsset[], second: readonly AdminAsset[]) {
  return [...new Map([...first, ...second].map((asset) => [asset.id, asset])).values()];
}

function clamp(value: number, minimum: number, maximum: number) {
  return Math.min(maximum, Math.max(minimum, value));
}
